import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GearDepartment } from "./gear/GearDepartment";
import GearFormDialog from "./gear/GearFormDialog";
import BudgetProgress from "./budget/BudgetProgress";
import { GearItem, CAMERA_CATEGORIES, GE_CATEGORIES } from "@/types/gear";
import { useAuth } from "@/components/auth/AuthProvider";
import { useGearItems } from "./gear/useGearItems";
import { useBudgetStatus } from "./gear/useBudgetStatus";
import { Card } from "./ui/card";
import { Calculator, Zap, Calendar } from "lucide-react";
import { AddFromInventoryDialog } from "./inventory/AddFromInventoryDialog";

interface GearListProps {
  projectId: string;
  budgetEnabled?: boolean;
  cameraEnabled?: boolean;
  geEnabled?: boolean;
  onAddItemClick?: () => void;
  isPublicView?: boolean;
}

export default function GearList({ 
  projectId, 
  budgetEnabled = true,
  cameraEnabled = true,
  geEnabled = true,
  onAddItemClick,
  isPublicView = false
}: GearListProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<GearItem | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<"camera" | "ge" | null>(null);
  const [cameraNotes, setCameraNotes] = useState("");
  const [geNotes, setGeNotes] = useState("");
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);

  const { user } = useAuth();

  const { data: gearItems, refetch } = useGearItems(projectId);
  const { data: budgetStatus } = useBudgetStatus(projectId, budgetEnabled);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!user) return;

      const { data: project } = await supabase
        .from("projects")
        .select("user_id")
        .eq("id", projectId)
        .single();

      if (project?.user_id === user.id) {
        setCanEdit(true);
        return;
      }

      const { data: share } = await supabase
        .from("project_shares")
        .select("permission")
        .eq("project_id", projectId)
        .eq("shared_with", user.id)
        .single();

      setCanEdit(share?.permission === "edit");
    };

    checkPermissions();
  }, [projectId, user]);

  useEffect(() => {
    if (!projectId) return;

    const loadProjectNotes = async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("camera_notes, ge_notes")
        .eq("id", projectId)
        .single();

      if (error) {
        toast.error("Failed to load department notes");
        return;
      }

      if (data) {
        setCameraNotes(data.camera_notes || "");
        setGeNotes(data.ge_notes || "");
      }
    };

    loadProjectNotes();
  }, [projectId]);

  const filteredGearItems = gearItems?.filter(item => 
    (cameraEnabled && CAMERA_CATEGORIES.includes(item.category as any)) ||
    (geEnabled && GE_CATEGORIES.includes(item.category as any))
  ) || [];

  const calculateDepartmentTotals = (items: GearItem[]) => {
    return items.reduce((totals, item) => {
      const total = (item.quantity || 0) * (item.cost_per_unit || 0) * (item.days_needed || 1);
      
      if (cameraEnabled && CAMERA_CATEGORIES.includes(item.category as any)) {
        totals.camera += total;
      }
      if (geEnabled && GE_CATEGORIES.includes(item.category as any)) {
        totals.ge += total;
      }
      
      return totals;
    }, { camera: 0, ge: 0 });
  };

  const departmentTotals = calculateDepartmentTotals(gearItems || []);
  
  const totalItems = filteredGearItems.length;
  const totalDays = filteredGearItems.length > 0
    ? Math.max(...filteredGearItems.map(item => item.days_needed || 1))
    : 0;

  const handleDelete = async (id: string) => {
    if (!canEdit) {
      toast.error(t('gear.list.no_permission_delete'));
      return;
    }

    try {
      const { error } = await supabase
        .from("gear_items")
        .delete()
        .eq("id", id)
        .eq("project_id", projectId);

      if (error) {
        console.error("Delete error:", error);
        toast.error(t('gear.list.failed_delete_item'));
        return;
      }

      toast.success(t('gear.list.item_deleted'));
      refetch();
    } catch (error) {
      console.error("Unexpected error during deletion:", error);
      toast.error(t('gear.list.unexpected_error'));
    }
  };

  const handleEdit = (item: GearItem) => {
    if (!canEdit) {
      toast.error("You don't have permission to edit items");
      return;
    }
    
    setEditingItem(item);
    // Determine department based on item category
    if (CAMERA_CATEGORIES.includes(item.category as any)) {
      setSelectedDepartment("camera");
    } else if (GE_CATEGORIES.includes(item.category as any)) {
      setSelectedDepartment("ge");
    }
    setShowForm(true);
  };

  const handleAddItemsFromInventory = async (items: any[]) => {
    if (!canEdit) {
      toast.error(t('gear.list.no_permission_add'));
      return;
    }

    if (items.length === 0) return;

    try {
      console.log('Adding items to project:', items);
      const itemsToAdd = items.map(item => ({
        project_id: projectId,
        name: item.name,
        category: item.category,
        quantity: item.quantity || 1,
        cost_per_unit: item.cost_per_unit || 0,
        notes: item.notes || null,
        days_needed: 1
      }));

      const { error } = await supabase
        .from("gear_items")
        .insert(itemsToAdd);

      if (error) {
        console.error("Add items error:", error);
        toast.error(t('gear.list.failed_add_items'));
        return;
      }

      toast.success(t('gear.list.items_added', { count: items.length }));
      refetch();
    } catch (error) {
      console.error("Unexpected error adding items:", error);
      toast.error(t('gear.list.unexpected_error'));
    }
  };

  const handleFormSubmit = async (item: Partial<GearItem>) => {
    if (!canEdit) {
      toast.error(t('gear.list.no_permission_modify'));
      return;
    }

    if (!item.name || !item.category) {
      toast.error(t('gear.list.name_category_required'));
      return;
    }

    try {
      if (editingItem) {
        const { error } = await supabase
          .from("gear_items")
          .update({
            name: item.name,
            category: item.category,
            quantity: item.quantity || 0,
            cost_per_unit: item.cost_per_unit || 0,
            notes: item.notes || null,
            vendor_id: item.vendor_id,
            days_needed: item.days_needed || 1
          })
          .eq("id", editingItem.id);

        if (error) {
          console.error("Update error:", error);
          toast.error(t('gear.list.failed_update_item'));
          return;
        }

        toast.success(t('gear.list.item_updated'));
      } else {
        const { error } = await supabase.from("gear_items").insert({
          project_id: projectId,
          name: item.name,
          category: item.category,
          quantity: item.quantity || 0,
          cost_per_unit: item.cost_per_unit || 0,
          notes: item.notes || null,
          vendor_id: item.vendor_id,
          days_needed: item.days_needed || 1
        });

        if (error) {
          console.error("Insert error:", error);
          toast.error(t('gear.list.failed_add_item'));
          return;
        }

        toast.success(t('gear.list.item_added'));
      }

      refetch();
      handleFormClose();
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error(t('gear.list.unexpected_error'));
    }
  };

  const handleNotesChange = async (department: "camera" | "ge", notes: string) => {
    if (!canEdit) {
      toast.error(t('gear.list.no_permission_edit_notes'));
      return;
    }

    const updateData = department === "camera" 
      ? { camera_notes: notes }
      : { ge_notes: notes };

    const { error } = await supabase
      .from("projects")
      .update(updateData)
      .eq("id", projectId);

    if (error) {
      console.error("Notes update error:", error);
      toast.error(t('gear.list.failed_update_notes', { department: t(`gear.list.${department}_department`) }));
      return;
    }

    if (department === "camera") {
      setCameraNotes(notes);
    } else {
      setGeNotes(notes);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingItem(null);
    setSelectedDepartment(null);
  };

  const handleAddItem = (department: "camera" | "ge") => {
    setSelectedDepartment(department);
    setShowForm(true);
    onAddItemClick && onAddItemClick();
  };

  const handleShowInventoryDialog = (department: "camera" | "ge") => {
    setSelectedDepartment(department);
    setShowInventoryDialog(true);
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {budgetEnabled && (
        <Card className="p-4 space-y-4 bg-card/50 backdrop-blur-sm border-white/10">
          <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-3">
            {cameraEnabled && (
              <BudgetProgress
                title={t('gear.list.camera_department')}
                budget={budgetStatus?.camera_budget || 0}
                spent={departmentTotals.camera}
                type="camera"
              />
            )}
            
            {geEnabled && (
              <BudgetProgress
                title={t('gear.list.ge_department')}
                budget={budgetStatus?.ge_budget || 0}
                spent={departmentTotals.ge}
                type="ge"
              />
            )}
            
            <BudgetProgress
              title={t('gear.list.total_budget')}
              budget={
                (cameraEnabled ? (budgetStatus?.camera_budget || 0) : 0) + 
                (geEnabled ? (budgetStatus?.ge_budget || 0) : 0)
              }
              spent={
                (cameraEnabled ? departmentTotals.camera : 0) + 
                (geEnabled ? departmentTotals.ge : 0)
              }
              type="total"
            />
          </div>
        </Card>
      )}

      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-lg font-semibold text-white">{t('gear.list.title')}</h2>
          <div className="flex flex-wrap gap-4 text-sm w-full sm:w-auto mb-6">
            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-md flex-1 sm:flex-initial justify-center sm:justify-start">
              <Calculator className="h-4 w-4 text-primary" />
              <span className="text-white/90">{t('gear.list.total_items')}:</span>
              <span className="font-medium text-white">{totalItems}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-md flex-1 sm:flex-initial justify-center sm:justify-start">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-white/90">{t('gear.list.days_needed')}:</span>
              <span className="font-medium text-white">{totalDays}</span>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {cameraEnabled && (
            <GearDepartment
              title={t('gear.list.camera_department')}
              items={gearItems || []}
              categories={CAMERA_CATEGORIES}
              notes={cameraNotes}
              onNotesChange={(notes) => handleNotesChange("camera", notes)}
              onAddItem={() => handleAddItem("camera")}
              onEditItem={handleEdit}
              onDeleteItem={handleDelete}
              canEdit={canEdit}
              showBudget={budgetEnabled}
              projectId={projectId}
              onAddFromInventory={() => handleShowInventoryDialog("camera")}
              isPublicView={isPublicView}
            />
          )}

          {geEnabled && (
            <GearDepartment
              title={t('gear.list.ge_department')}
              items={gearItems || []}
              categories={GE_CATEGORIES}
              notes={geNotes}
              onNotesChange={(notes) => handleNotesChange("ge", notes)}
              onAddItem={() => handleAddItem("ge")}
              onEditItem={handleEdit}
              onDeleteItem={handleDelete}
              canEdit={canEdit}
              showBudget={budgetEnabled}
              projectId={projectId}
              onAddFromInventory={() => handleShowInventoryDialog("ge")}
              isPublicView={isPublicView}
            />
          )}
        </div>
      </div>

      {(showForm || editingItem) && canEdit && !isPublicView && (
        <GearFormDialog
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={editingItem || undefined}
          showBudget={budgetEnabled}
          department={selectedDepartment}
          projectId={projectId}
        />
      )}

      {!isPublicView && (
        <AddFromInventoryDialog
          open={showInventoryDialog}
          onOpenChange={setShowInventoryDialog}
          onAddItems={handleAddItemsFromInventory}
          projectId={projectId}
        />
      )}
    </div>
  );
}
